/**
 * GATE5-FIN-09: COA Activation Maker-Checker Workspace
 * Route: /finance/coa-activation
 * Cap: coa.read (view), coa.activate (review + execute), coa.manage (submits via Gate 2D)
 *
 * Resolves FIN-08-F01: enforces dual-control for COA version activation.
 * Maker (coa.manage) submits → Checker (coa.activate, DIFFERENT user) approves → executes.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { toast } from "sonner";
import {
  ShieldCheck, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, ChevronRight, Loader2, User, Calendar,
  FileText, PlayCircle, RotateCcw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── API helpers ──────────────────────────────────────────────────────────────

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...options.headers },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

const coaActivationApi = {
  list: (params = "") => apiFetch(`/api/finance/coa-activation-requests${params}`),
  get:  (rid)          => apiFetch(`/api/finance/coa-activation-requests/${rid}`),
  review: (rid, body)  => apiFetch(`/api/finance/coa-activation-requests/${rid}/review`,  { method: "POST", body: JSON.stringify(body) }),
  execute: (rid, body) => apiFetch(`/api/finance/coa-activation-requests/${rid}/execute`, { method: "POST", body: JSON.stringify(body) }),
  versions: ()         => apiFetch(`/api/finance/coa/ueim-coa-prashasta-001/versions`),
};

// ── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  PENDING_REVIEW: { label: "Pending Review", color: "text-[#8C4B00]", bg: "bg-[#FFF4E5]",  icon: Clock },
  APPROVED:       { label: "Approved",       color: "text-[#0176D3]", bg: "bg-[#E8F4FD]",  icon: CheckCircle },
  REJECTED:       { label: "Rejected",       color: "text-[#BA0517]", bg: "bg-[#FCEBEC]",  icon: XCircle },
  EXPIRED:        { label: "Expired",        color: "text-[#706E6B]", bg: "bg-[#F3F3F3]",  icon: AlertCircle },
  EXECUTING:      { label: "Executing…",     color: "text-[#0176D3]", bg: "bg-[#E8F4FD]",  icon: Loader2 },
  EXECUTED:       { label: "Activated",      color: "text-[#2E844A]", bg: "bg-[#EAF5EE]",  icon: ShieldCheck },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.PENDING_REVIEW;
  const Icon = cfg.icon;
  return (
    <span data-testid={`coa-req-status-${status?.toLowerCase()}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

const COA_VERSION_STATUS = {
  ACTIVE:  { label: "Active",   color: "text-[#2E844A]", bg: "bg-[#EAF5EE]" },
  DRAFT:   { label: "Draft",    color: "text-[#8C4B00]", bg: "bg-[#FFF4E5]" },
  RETIRED: { label: "Retired",  color: "text-[#706E6B]", bg: "bg-[#F3F3F3]" },
};

function VersionBadge({ status }) {
  const cfg = COA_VERSION_STATUS[status] || COA_VERSION_STATUS.DRAFT;
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
  );
}

// ── Review modal ─────────────────────────────────────────────────────────────

function ReviewModal({ req, onClose, onDone }) {
  const [decision, setDecision] = useState("");
  const [notes, setNotes]       = useState("");
  const [loading, setLoading]   = useState(false);

  const submit = async () => {
    if (!decision) { toast.error("Select Approve or Reject"); return; }
    setLoading(true);
    try {
      await coaActivationApi.review(req.id, { decision, notes: notes || null });
      toast.success(`Request ${decision === "APPROVED" ? "approved" : "rejected"} successfully`);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="font-semibold text-[#181818] text-lg">Review Activation Request</h3>
        <div className="text-sm text-[#444]">
          <p>Version <strong>{req.version_number}</strong> submitted by <strong>{req.requested_by_email}</strong></p>
          <p className="text-xs text-[#706E6B] mt-1">{req.justification}</p>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">Decision</p>
          <div className="flex gap-3">
            {["APPROVED","REJECTED"].map(d => (
              <button key={d}
                data-testid={`review-decision-${d.toLowerCase()}`}
                onClick={() => setDecision(d)}
                className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-all
                  ${decision === d
                    ? d === "APPROVED" ? "bg-[#EAF5EE] border-[#2E844A] text-[#2E844A]" : "bg-[#FCEBEC] border-[#BA0517] text-[#BA0517]"
                    : "border-[#E0E0E0] text-[#444] hover:border-[#999]"}`}>
                {d === "APPROVED" ? "Approve" : "Reject"}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Review Notes</label>
          <textarea
            data-testid="review-notes-input"
            value={notes} onChange={e => setNotes(e.target.value)}
            rows={3} placeholder="Optional notes for the audit trail…"
            className="w-full border border-[#E0E0E0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:border-[#0176D3]"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-[#E0E0E0] text-sm text-[#444] hover:bg-[#F3F3F3]">
            Cancel
          </button>
          <button onClick={submit} disabled={loading}
            data-testid="review-submit-btn"
            className="flex-1 py-2 rounded-lg bg-[#032D60] text-white text-sm font-semibold hover:bg-[#0176D3] disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 size={14} className="animate-spin" />}
            Submit Review
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Execute confirmation modal ────────────────────────────────────────────────

function ExecuteModal({ req, onClose, onDone }) {
  const [notes, setNotes]     = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const result = await coaActivationApi.execute(req.id, { notes: notes || null });
      toast.success(result.message || "COA Version activated successfully");
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#BA0517]">
          <AlertCircle size={20} />
          <h3 className="font-semibold text-lg">Execute COA Activation</h3>
        </div>
        <div className="bg-[#FFF4E5] border border-[#F5A623] rounded-lg p-3 text-sm text-[#8C4B00] space-y-1">
          <p className="font-semibold">This is a controlled financial configuration change.</p>
          <p>COA Version <strong>{req.version_number}</strong> will become <strong>ACTIVE</strong>.</p>
          <p>COA Version 1 will be <strong>RETIRED</strong>.</p>
          <p>All 20 accounting rules remain DRAFT — no journal postings will occur.</p>
          <p>Approved by: <strong>{req.reviewed_by_email}</strong></p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Execution Notes</label>
          <textarea
            data-testid="execute-notes-input"
            value={notes} onChange={e => setNotes(e.target.value)}
            rows={2} placeholder="Optional execution notes for the audit trail…"
            className="w-full border border-[#E0E0E0] rounded-lg p-2 text-sm resize-none focus:outline-none focus:border-[#0176D3]"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-[#E0E0E0] text-sm text-[#444] hover:bg-[#F3F3F3]">
            Cancel
          </button>
          <button onClick={submit} disabled={loading}
            data-testid="execute-activate-btn"
            className="flex-1 py-2 rounded-lg bg-[#BA0517] text-white text-sm font-semibold hover:bg-[#8E0517] disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 size={14} className="animate-spin" />}
            Execute Activation
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Request row ───────────────────────────────────────────────────────────────

function RequestRow({ req, canActivate, userId, onReview, onExecute }) {
  const isSelf = req.requested_by === userId;
  const canReview  = canActivate && req.status === "PENDING_REVIEW" && !isSelf;
  const canExecute = canActivate && req.status === "APPROVED";

  return (
    <div data-testid={`coa-req-row-${req.id?.slice(-6)}`}
      className="bg-white rounded-xl border border-[#E0E0E0] p-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[#032D60] text-sm">
            COA v{req.version_number}
          </span>
          <StatusBadge status={req.status} />
          {isSelf && (
            <span className="text-[10px] bg-[#F3F3F3] text-[#706E6B] px-1.5 py-0.5 rounded">
              My submission
            </span>
          )}
        </div>
        <p className="text-xs text-[#444] line-clamp-2">{req.justification}</p>
        <div className="flex flex-wrap gap-3 text-xs text-[#706E6B]">
          <span className="flex items-center gap-1"><User size={11} />{req.requested_by_email}</span>
          <span className="flex items-center gap-1"><Calendar size={11} />{req.requested_at?.slice(0,10)}</span>
          {req.reviewed_by_email && (
            <span className="flex items-center gap-1"><CheckCircle size={11} />{req.reviewed_by_email}</span>
          )}
        </div>
        {req.review_note && (
          <p className="text-xs text-[#444] italic mt-1">"{req.review_note}"</p>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        {canReview && (
          <button onClick={() => onReview(req)}
            data-testid={`review-btn-${req.id?.slice(-6)}`}
            className="px-3 py-1.5 bg-[#032D60] text-white text-xs font-semibold rounded-lg hover:bg-[#0176D3] flex items-center gap-1">
            <ShieldCheck size={13} /> Review
          </button>
        )}
        {isSelf && req.status === "PENDING_REVIEW" && (
          <span className="px-3 py-1.5 text-xs text-[#8C4B00] bg-[#FFF4E5] rounded-lg border border-[#F5A623]">
            SoD: Awaiting different approver
          </span>
        )}
        {canExecute && (
          <button onClick={() => onExecute(req)}
            data-testid={`execute-btn-${req.id?.slice(-6)}`}
            className="px-3 py-1.5 bg-[#2E844A] text-white text-xs font-semibold rounded-lg hover:bg-[#1D6B38] flex items-center gap-1">
            <PlayCircle size={13} /> Execute
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function COAActivationWorkspace() {
  const { user } = useAuth();
  const canRead     = hasCapability(user, "coa.read");
  const canActivate = hasCapability(user, "coa.activate");
  const canManage   = hasCapability(user, "coa.manage");

  const [versions, setVersions]   = useState([]);
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [reviewReq, setReviewReq] = useState(null);
  const [execReq, setExecReq]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, rRes] = await Promise.all([
        coaActivationApi.versions().catch(() => []),
        canRead ? coaActivationApi.list() : [],
      ]);
      setVersions(Array.isArray(vRes) ? vRes : []);
      setRequests(Array.isArray(rRes) ? rRes : []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => { load(); }, [load]);

  const activeVersion = versions.find(v => v.status === "ACTIVE");
  const draftVersions = versions.filter(v => v.status === "DRAFT");

  return (
    <div className="min-h-screen bg-[#F8F9FB] p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs text-[#706E6B] mb-1">
              <span>Finance</span><ChevronRight size={12} /><span>COA Activation</span>
            </div>
            <h1 className="text-2xl font-bold text-[#032D60]">COA Activation Workspace</h1>
            <p className="text-sm text-[#706E6B] mt-0.5">
              Maker-checker dual-control for Chart of Accounts version activation (GATE5-FIN-09)
            </p>
          </div>
          <button onClick={load} disabled={loading}
            data-testid="coa-refresh-btn"
            className="p-2 rounded-lg border border-[#E0E0E0] hover:bg-[#F3F3F3]">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* Capability banner */}
        <div className={`rounded-xl p-3 text-sm flex items-center gap-2
          ${canActivate
            ? "bg-[#EAF5EE] text-[#2E844A] border border-[#A8D5B3]"
            : "bg-[#E8F4FD] text-[#0176D3] border border-[#91C9F5]"}`}>
          <ShieldCheck size={16} />
          {canActivate
            ? "You hold coa.activate — you can review requests and execute activation."
            : canManage
              ? "You hold coa.manage — you can submit activation requests. A different user with coa.activate will review."
              : "Read-only view."}
        </div>

        {/* COA Version State */}
        <div>
          <h2 className="text-sm font-semibold text-[#444] mb-2 uppercase tracking-wide">
            Current COA Versions
          </h2>
          {loading ? (
            <div className="bg-white rounded-xl border border-[#E0E0E0] p-6 text-center">
              <Loader2 size={20} className="animate-spin mx-auto text-[#706E6B]" />
            </div>
          ) : (
            <div className="space-y-2" data-testid="coa-versions-list">
              {versions.map(v => (
                <div key={v.id || v.version_number}
                  data-testid={`coa-version-v${v.version_number}`}
                  className="bg-white rounded-xl border border-[#E0E0E0] p-4 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#032D60]">Version {v.version_number}</span>
                      <VersionBadge status={v.status} />
                    </div>
                    <p className="text-xs text-[#706E6B]">{v.name || v.description}</p>
                    {v.activated_at && (
                      <p className="text-xs text-[#444]">Activated: {v.activated_at?.slice(0,10)}</p>
                    )}
                  </div>
                  <div className="text-right text-xs text-[#706E6B]">
                    <p>ID: {v.id}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SoD info box */}
        <div className="bg-white rounded-xl border border-[#E0E0E0] p-4 space-y-2">
          <h2 className="text-sm font-semibold text-[#444] flex items-center gap-2">
            <ShieldCheck size={15} className="text-[#032D60]" />
            Dual-Control Rules (GATE5-FIN-09)
          </h2>
          <ul className="text-xs text-[#444] space-y-1 pl-4 list-disc">
            <li><strong>coa.manage</strong> — Submit an activation request (Gate 2D)</li>
            <li><strong>coa.activate</strong> — Review (approve/reject) and execute — must be a DIFFERENT user than the submitter</li>
            <li>Self-approval is blocked at the service layer (SoD enforced)</li>
            <li>Only one APPROVED request is permitted per COA version at a time</li>
            <li>COA version must be DRAFT at approval time and at execution time (stale approval protection)</li>
            <li>Execution is atomic — concurrent attempts are blocked</li>
            <li>Legacy single-actor activate endpoint is disabled (returns 409)</li>
          </ul>
        </div>

        {/* Activation Requests */}
        <div>
          <h2 className="text-sm font-semibold text-[#444] mb-2 uppercase tracking-wide">
            Activation Requests
          </h2>
          {loading ? (
            <div className="bg-white rounded-xl border border-[#E0E0E0] p-6 text-center">
              <Loader2 size={20} className="animate-spin mx-auto text-[#706E6B]" />
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E0E0E0] p-8 text-center">
              <FileText size={24} className="mx-auto mb-2 text-[#706E6B]" />
              <p className="text-sm text-[#706E6B]">No activation requests found.</p>
              {canManage && (
                <p className="text-xs text-[#706E6B] mt-1">
                  Submit a request via Gate 2D: COA Migration workspace.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="coa-requests-list">
              {requests.map(req => (
                <RequestRow key={req.id}
                  req={req}
                  canActivate={canActivate}
                  userId={user?.id}
                  onReview={r => setReviewReq(r)}
                  onExecute={r => setExecReq(r)}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Modals */}
      {reviewReq && (
        <ReviewModal req={reviewReq} onClose={() => setReviewReq(null)} onDone={() => { setReviewReq(null); load(); }} />
      )}
      {execReq && (
        <ExecuteModal req={execReq} onClose={() => setExecReq(null)} onDone={() => { setExecReq(null); load(); }} />
      )}
    </div>
  );
}
